import React from 'react';
import sortBy from 'lodash/sortBy';
import { TransitionGroup } from 'react-transition-group';
import { Collapse, List, styled, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAppMode, useCreateTicketCrumb, useSelectedComment, useTicket } from '@tymely/services';
import { IArgument, IArgumentUpdate, IDineshTicketOperations, requiresInput } from '@tymely/atoms';
import { yellow } from '@mui/material/colors';
import { getArgumentFindFunction } from '@tymely/utils/arguments';

import { getDisplayRegulatorArg, getGroupArg, getSearchArg } from './utils';
import ArgumentListItem from './ArgumentListItem';
import { useArgumentsTabsContext } from './ArgumentsTabsProvider';
import useArgumentsQuery from './useArgumentsQuery';

const _List = styled(List)`
    padding: 0;
`;

const _Collapse = styled(Collapse)`
    background: ${yellow[200]};
    &.MuiCollapse-entered {
        background: 0;
    }
    &.MuiCollapse-hidden {
        background: 0;
    }
`;

type ArgumentsProps = {
    argumentItems: IArgument[];
    withCopy?: boolean;
    disabled?: boolean;
    loading?: boolean;
    touchedArgIds?: Set<IArgument['md_id']>;
    approvedArgs?: Set<IArgument['md_id']>;
    onTouch?: (updates: IArgument) => void;
};

const ArgumentList = React.memo(
    ({
        argumentItems,
        withCopy,
        disabled,
        loading,
        touchedArgIds = new Set<IArgument['md_id']>(),
        approvedArgs,
        onTouch,
    }: ArgumentsProps) => {
        const [argsOrder, setArgsOrder] = React.useState<IArgument['md_id'][]>([]);

        const { version, onUpdate } = useArgumentsTabsContext();
        const argsQuery = useArgumentsQuery({ version });
        const allArgs = argsQuery.data ?? [];

        React.useEffect(() => {
            setArgsOrder((order) =>
                order.concat(
                    sortBy(
                        argumentItems.map((arg) => arg.md_id).filter((md_id) => !order.includes(md_id)),
                        (md_id) => (approvedArgs?.has(md_id) ? 0 : 1),
                    ),
                ),
            );
        }, [argumentItems, setArgsOrder]);

        const args = React.useMemo(
            () => sortBy(argumentItems, (arg) => argsOrder.findIndex((md_id) => md_id === arg.md_id)),
            [argumentItems, argsOrder],
        );

        const ticket = useTicket();
        const createTicketCrumb = useCreateTicketCrumb();
        const selectedComment = useSelectedComment();
        const { isAnalystMode } = useAppMode();

        const firstUnapprovedArgAt = React.useMemo(() => {
            const unapprovedAt = args.findIndex(
                (arg) => !approvedArgs || !approvedArgs.has(arg.md_id) || requiresInput(arg),
            );
            return unapprovedAt >= 0 ? unapprovedAt : args.length;
        }, [args, approvedArgs]);

        const [lastUpdatedArgs, setLastUpdatedArgs] = React.useState<IArgument[]>([]);

        const _onUpdate = React.useCallback(
            (updates: IArgumentUpdate[]) => {
                for (const update of updates) {
                    const arg = allArgs.find((a) => a.id === update.id);
                    if (arg && !update.isApproval) {
                        createTicketCrumb(IDineshTicketOperations.USER_EDITED_TICKET_ARGUMENTS, {
                            ticket_id: ticket?.id ?? null,
                            argument_id: arg.id,
                            arg_name: arg.name,
                            argument_value: update.special_value || update.value,
                            argument_prev_value: arg.value,
                            comment_id: selectedComment?.id ?? null,
                            ticket_intent: selectedComment?.selected_intent_id ?? null,
                        });
                    }
                }
                setLastUpdatedArgs(updates.map((update) => allArgs.find((a) => a.id === update.id)).filter(Boolean));
                onUpdate?.(updates);
            },
            [allArgs, createTicketCrumb, selectedComment, onUpdate, setLastUpdatedArgs],
        );

        const withApproval = !!approvedArgs;

        return (
            <>
                {args.length === 0 && !argsQuery.isLoading && <Typography variant="body2">No items</Typography>}
                {argsQuery.isLoading && (
                    <Typography variant="body2" color="text.secondary">
                        Loading arguments...
                    </Typography>
                )}
                {args.length > 0 && (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <_List dense>
                            <TransitionGroup appear={false} exit={false}>
                                {args.map((arg, index, array) => {
                                    const groupArgument = getGroupArg(arg, allArgs);
                                    const searchArgument = getSearchArg(groupArgument ?? arg, allArgs);
                                    const displayRegulatorArgument = getDisplayRegulatorArg(
                                        groupArgument ?? arg,
                                        allArgs,
                                    );
                                    const approved = approvedArgs?.has(arg.md_id) && !requiresInput(arg);
                                    const isLoadingAfterAccessoryArgumentChange =
                                        loading && !!lastUpdatedArgs.find(getArgumentFindFunction(arg));
                                    const isEditable =
                                        (!disabled && !withApproval) ||
                                        (approved && index < firstUnapprovedArgAt) ||
                                        index === firstUnapprovedArgAt;
                                    const onItemUpdate = (argUpdate: IArgumentUpdate[]) => {
                                        _onUpdate(argUpdate);
                                        onTouch?.(arg);
                                    };
                                    const withSeparator = array[index - 1] && array[index - 1].rank !== arg.rank;
                                    const className = onTouch
                                        ? touchedArgIds.has(arg.md_id)
                                            ? 'touched'
                                            : 'untouched'
                                        : undefined;

                                    return (
                                        <_Collapse key={arg.name} className={className} onClick={() => onTouch?.(arg)}>
                                            <ArgumentListItem
                                                withSeparator={withSeparator}
                                                argument={arg}
                                                groupArgument={groupArgument}
                                                displayRegulatorArgument={displayRegulatorArgument}
                                                searchArgument={searchArgument}
                                                withApproval={withApproval && (isAnalystMode || !arg.is_stale)}
                                                approved={approved}
                                                editable={isEditable && !disabled && (isAnalystMode || !arg.is_stale)}
                                                loading={isLoadingAfterAccessoryArgumentChange}
                                                withCopy={withCopy}
                                                onUpdate={onItemUpdate}
                                            />
                                        </_Collapse>
                                    );
                                })}
                            </TransitionGroup>
                        </_List>
                    </LocalizationProvider>
                )}
            </>
        );
    },
);

ArgumentList.displayName = 'ArgumentList';

export default ArgumentList;
